import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import {
  Bank,
  CommentHistoryRow,
  CustomerService,
  EmployeeService,
  OperationService,
  PaymentTypePipe,
  trackByIndex,
  User,
} from '@beautyapp/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
} from '@angular/forms';
import { Observable, of, Subject } from 'rxjs';
import { NzDrawerService } from 'ng-zorro-antd/drawer';
import { NzModalService } from 'ng-zorro-antd/modal';
import { switchMap, takeUntil } from 'rxjs/operators';
import { OperationDetailComponent } from '../operation-detail/operation-detail.component';
import startOfMonth from 'date-fns/startOfMonth';
import endOfMonth from 'date-fns/endOfMonth';
import subMonths from 'date-fns/subMonths';
import startOfYear from 'date-fns/startOfYear';
import endOfYear from 'date-fns/endOfYear';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { NgxPermissionsService } from 'ngx-permissions';
import { OperationDeleteComponent } from '../operation-delete/operation-delete.component';
import { UserViewComponent } from '@beautyapp/users';
import { openPackagePurchaseDrawer } from '@beautyapp/service-packages';
import { format } from 'date-fns';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzFormModule } from 'ng-zorro-antd/form';
import {
  InputComponent,
  SelectComponent,
  SelectSearchComponent,
} from '@beautyapp/form';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { AsyncPipe, DatePipe, DecimalPipe, SlicePipe } from '@angular/common';
import { NgxPermissionsModule } from 'ngx-permissions';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzPopoverDirective } from 'ng-zorro-antd/popover';
import { NzAlertComponent } from 'ng-zorro-antd/alert';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';

@Component({
  selector: 'beautyapp-operations',
  templateUrl: './operations.component.html',
  styleUrls: ['./operations.component.scss'],
  standalone: true,
  imports: [
    NzGridModule,
    NzFormModule,
    InputComponent,
    SelectComponent,
    NzDatePickerModule,
    TranslatePipe,
    ReactiveFormsModule,
    SelectSearchComponent,
    NzCheckboxModule,
    NzSpaceModule,
    NzButtonModule,
    AsyncPipe,
    NgxPermissionsModule,
    NzTooltipDirective,
    NzIconDirective,
    NzTableModule,
    PaymentTypePipe,
    DatePipe,
    NzTagModule,
    SlicePipe,
    NzPopoverDirective,
    NzAlertComponent,
    FormsModule,
    DecimalPipe,
    NzInputModule,
  ],
})
export class OperationsComponent implements OnInit, OnDestroy {
  private service: OperationService = inject(OperationService);
  private modalService: NzModalService = inject(NzModalService);
  private drawerService: NzDrawerService = inject(NzDrawerService);
  private customerService: CustomerService = inject(CustomerService);
  private employeeService: EmployeeService = inject(EmployeeService);
  private fb: UntypedFormBuilder = inject(UntypedFormBuilder);
  private translateService: TranslateService = inject(TranslateService);
  private permissionsService = inject(NgxPermissionsService);
  private message: NzMessageService = inject(NzMessageService);

  loading = false;
  loadData: any[] = [];
  totals: any = {};
  total = 1;
  paginatorPageSize = 10;
  paginatorPageIndex = 1;
  searchForm: UntypedFormGroup;
  searchValue?: string;
  branchId?: string;
  sub$: Subject<any> = new Subject<any>();
  branches$!: Observable<any>;
  customers$?: Observable<User[] | null>;
  employees$?: Observable<any>;
  today = format(new Date(), 'yyyy-MM-dd');
  ranges = {
    დღეს: [new Date(), new Date()],
    'მიმდინარე თვე': [startOfMonth(new Date()), endOfMonth(new Date())],
    'წინა თვე': [
      startOfMonth(subMonths(new Date(), 1)),
      endOfMonth(subMonths(new Date(), 1)),
    ],
    'მიმდინარე წელი': [startOfYear(new Date()), endOfYear(new Date())],
  };
  date: any;
  dateFormat = 'dd/MM/yyyy';

  // Rebuilt reactively on onLangChange / onTranslationChange so the dropdowns
  // never show raw keys (PAYMENTTYPE.1, statistic.operation.operationSource.*)
  // when the translation bundle hasn't loaded yet at construction time after a
  // hard reload.
  operationSource: { id: string; name: string }[] = this.buildOperationSource();
  paymentTypes: { id: number; name: string }[] = this.buildPaymentTypes();
  // Bank enum values double as their own labels (TBC, BOG, ...); no translation
  // convention exists for banks, so the raw enum value is shown.
  banks: { id: string; name: string }[] = Object.values(Bank).map((b) => ({
    id: b,
    name: b,
  }));

  private readonly _translationSync = this.translateService.onLangChange
    .pipe(takeUntil(this.sub$))
    .subscribe(() => {
      this.operationSource = this.buildOperationSource();
      this.paymentTypes = this.buildPaymentTypes();
    });
  private readonly _translationLoadedSync =
    this.translateService.onTranslationChange
      .pipe(takeUntil(this.sub$))
      .subscribe(() => {
        this.operationSource = this.buildOperationSource();
        this.paymentTypes = this.buildPaymentTypes();
      });
  private readonly _translationDefaultLangSync =
    this.translateService.onDefaultLangChange
      .pipe(takeUntil(this.sub$))
      .subscribe(() => {
        this.operationSource = this.buildOperationSource();
        this.paymentTypes = this.buildPaymentTypes();
      });

  private buildOperationSource(): { id: string; name: string }[] {
    const t = (key: string) => this.translateService.instant(key);
    return [
      {
        id: 'BOOKING',
        name: t('statistic.operation.operationSource.reservation'),
      },
      { id: 'SALARY', name: t('statistic.operation.operationSource.salary') },
      {
        id: 'REALIZATION',
        name: t('statistic.operation.operationSource.realization'),
      },
      { id: 'EXPENCE', name: t('statistic.operation.operationSource.expense') },
      {
        id: 'PURCHASE',
        name: t('statistic.operation.operationSource.purchase'),
      },
      {
        id: 'BALANCE',
        name: t('statistic.operation.operationSource.popOpBalance'),
      },
      {
        id: 'BALANCERETURN',
        name: t('statistic.operation.operationSource.returnBalance'),
      },
      {
        id: 'CASHIER',
        name: t('statistic.operation.operationSource.cashRegister'),
      },
      { id: 'SERVICE_PACKAGE', name: 'სერვისის პაკეტი' },
    ];
  }

  private buildPaymentTypes(): { id: number; name: string }[] {
    const t = (key: string) => this.translateService.instant(key);
    return [
      { id: 1, name: t('PAYMENTTYPE.1') },
      { id: 2, name: t('PAYMENTTYPE.2') },
      { id: 3, name: t('PAYMENTTYPE.3') },
      { id: 4, name: t('PAYMENTTYPE.4') },
      { id: 5, name: t('PAYMENTTYPE.5') },
      { id: 6, name: t('PAYMENTTYPE.6') },
      { id: 7, name: t('PAYMENTTYPE.7') },
      { id: 8, name: t('PAYMENTTYPE.8') },
      { id: 11, name: t('PAYMENTTYPE.11') },
    ];
  }

  trackByIndex = trackByIndex;

  // beaut-307: expandable per-bank details block for BALANCE (top-up) rows.
  expandSet = new Set<number>();

  onExpandChange(id: number, checked: boolean): void {
    if (checked) {
      this.expandSet.add(id);
    } else {
      this.expandSet.delete(id);
    }
  }

  // beaut-286: inline comment edit (balance operations only, Admin/Administrator/
  // Manager — same role gate as canDelete). editingCommentId tracks which row
  // is currently in edit mode; only one row can be edited at a time.
  editingCommentId: number | null = null;
  editingCommentValue = '';
  savingComment = false;

  // beaut-286: comment change history, loaded on demand per row (popover).
  historyRowId: number | null = null;
  historyLoading = false;
  historyRows: CommentHistoryRow[] = [];

  // Cache of employees loaded for the search dropdown, reused to resolve a
  // comment-history changer id -> display name when possible (see
  // resolveChangerName). Best-effort only — falls back to the raw id.
  private employeesCache: any[] = [];

  canEditComment(row: any): boolean {
    if (row?.operationSource !== 'BALANCE') {
      return false;
    }
    const perms = this.permissionsService.getPermissions();
    return !!(perms['Admin'] || perms['Administrator'] || perms['Manager']);
  }

  startEditComment(row: any): void {
    this.editingCommentId = row.id;
    this.editingCommentValue = row.description || '';
  }

  cancelEditComment(): void {
    this.editingCommentId = null;
    this.editingCommentValue = '';
  }

  saveEditComment(row: any): void {
    if (this.savingComment) {
      return;
    }
    this.savingComment = true;
    // Capture which row this save belongs to: the user may cancel this edit
    // and start editing a different row before the request resolves, so the
    // success handler must not clobber that newer edit session.
    const savedId = row.id;
    this.service
      .updateComment(row.id, this.editingCommentValue)
      .pipe(takeUntil(this.sub$))
      .subscribe({
        next: () => {
          this.savingComment = false;
          if (this.editingCommentId === savedId) {
            this.editingCommentId = null;
            this.editingCommentValue = '';
          }
          this.getOperations();
        },
        error: () => {
          this.savingComment = false;
          this.message.error(
            this.translateService.instant('operation.editCommentFailed')
          );
        },
      });
  }

  onHistoryVisibleChange(row: any, visible: boolean): void {
    if (!visible) {
      return;
    }
    this.historyRowId = row.id;
    this.historyLoading = true;
    this.historyRows = [];
    this.service
      .getCommentHistory(row.id)
      .pipe(takeUntil(this.sub$))
      .subscribe({
        next: (rows) => {
          // The popover for a different row may have opened (and started its
          // own fetch) before this one resolved — only apply the response if
          // it's still the active row.
          if (this.historyRowId !== row.id) {
            return;
          }
          this.historyLoading = false;
          this.historyRows = rows || [];
        },
        error: () => {
          if (this.historyRowId !== row.id) {
            return;
          }
          this.historyLoading = false;
          this.message.error(
            this.translateService.instant('operation.editCommentFailed')
          );
        },
      });
  }

  resolveChangerName(row: any, changedById: string): string {
    if (row?.createdBy?.id === changedById && row?.createdBy?.fullName) {
      return row.createdBy.fullName;
    }
    if (row?.deletedBy?.id === changedById && row?.deletedBy?.fullName) {
      return row.deletedBy.fullName;
    }
    const employee = this.employeesCache.find(
      (e: any) => e?.user?.id === changedById
    );
    if (employee?.fullName) {
      return employee.fullName;
    }
    return changedById;
  }

  constructor() {
    this.searchForm = this.fb.group({
      date: { begin: startOfMonth(new Date()), end: new Date() },
      paymentType: null,
      operationId: null,
      serviceId: null,
      employeeId: null,
      userId: null,
      user: null,
      operationSource: null,
      bankId: null,
      isDeleted: false,
      onlyTransactions: false,
      isVatIncluded: false,
    });
  }

  ngOnInit() {
    this.getEmployees();
  }

  private getOperations() {
    this.loading = true;
    const params = {
      page: this.paginatorPageIndex,
      pageSize: this.paginatorPageSize,
      search: this.searchForm.value || null,
      // bankId must be a TOP-LEVEL param: the backend reads it from
      // pageOptionsDto.bankId (not search.bankId) and getHttpParams only
      // flattens top-level array keys into repeated query params.
      bankId: this.searchForm.value.bankId,
      isDeleted: this.searchForm.value.isDeleted,
      onlyTransactions: this.searchForm.value.onlyTransactions,
    };
    this.service
      .get(params)
      .pipe(takeUntil(this.sub$))
      .subscribe((res) => {
        this.loading = false;
        this.loadData = res.data;
        this.total = res.meta.itemCount;
        this.totals = res.totals || {};
      });
  }

  searchCustomer(search: any) {
    this.customers$ = this.customerService
      .search(search)
      .pipe(takeUntil(this.sub$));
  }

  getEmployees() {
    this.employees$ = this.employeeService.getDropdown().pipe(
      takeUntil(this.sub$),
      switchMap((res) => {
        const employees = res.map((m: any) => ({
          ...m,
          fullName: m.user?.fullName,
        }));
        // beaut-286: cache for best-effort changedById -> name resolution in
        // the comment-history popover (see resolveChangerName).
        this.employeesCache = employees;
        return of(employees);
      })
    );
  }

  paginatorChange(pageEvent: any) {
    this.paginatorPageSize = pageEvent.pageSize;
    this.paginatorPageIndex = pageEvent.pageIndex;
    this.getOperations();
  }

  // beaut-359: Admin/Manager may delete any operation; Administrator only
  // today's; anyone else, no delete icon. Mirrors the backend rule.
  canDelete(row: any): boolean {
    const perms = this.permissionsService.getPermissions();
    if (perms['Admin'] || perms['Manager']) {
      return true;
    }
    if (perms['Administrator']) {
      const created = new Date(row?.createdAt);
      const now = new Date();
      return (
        created.getFullYear() === now.getFullYear() &&
        created.getMonth() === now.getMonth() &&
        created.getDate() === now.getDate()
      );
    }
    return false;
  }

  delete(item: any) {
    if (item.id) {
      this.modalService.create({
        nzTitle: this.translateService.instant('buttons.deletePop'),
        nzContent: OperationDeleteComponent,
        nzWidth: '600px',
        nzStyle: { top: '0px' },
        nzData: {
          operationId: item.id,
        },
        nzOnOk: () => {
          this.getOperations();
        },
      });
    }
  }

  view(event: any) {
    const sourceName = this.translateService.instant(
      'OPERATION_SOURCE.' + event.operationSource
    );
    const title = `${sourceName}  # ${event.id}`;

    const drawerRef = this.drawerService.create<
      OperationDetailComponent,
      { operationId: number },
      string
    >({
      nzTitle: title,
      nzContent: OperationDetailComponent,
      nzWrapClassName: 'user-drawer',
      nzBodyStyle: {
        padding: '0 15px 15px 15px',
      },
      nzData: {
        operationId: event.id,
      },
    });

    drawerRef.afterClose.pipe(takeUntil(this.sub$)).subscribe((data) => {
      console.log(data);
      if (typeof data === 'string') {
        console.log(data);
      }
    });
  }

  search() {
    console.log(this.searchForm.value);
    if (this.searchForm.value) {
      this.searchValue = this.searchForm.value.search || 'undefined';
      this.branchId = this.searchForm.value.branch || 'undefined';
      this.getOperations();
    }
  }

  export() {
    const params = {
      search: this.searchForm.value || null,
      // Top-level bankId — mirrors getOperations(); backend export reads
      // pageOptionsDto.bankId, not search.bankId.
      bankId: this.searchForm.value.bankId,
      isDeleted: this.searchForm.value.isDeleted,
    };
    return this.service.export('Operations.xlsx', params);
  }

  branchSelected($event: any) {
    this.branchId = $event;
    this.getOperations();
  }

  clearSearch() {
    this.searchForm.patchValue({
      date: { begin: startOfMonth(new Date()), end: new Date() },
      paymentType: null,
      operationId: null,
      serviceId: null,
      employeeId: null,
      userId: null,
      user: null,
      operationSource: null,
      bankId: null,
      isDeleted: false,
      onlyTransactions: false,
      isVatIncluded: false,
    });
    // The range-picker is bound to the standalone `date` model (not the form),
    // so patching the form above doesn't clear it — reset it explicitly or the
    // start/end date filter stays visually populated after Clear (beaut-283).
    this.date = null;
    this.search();
    this.searchValue = undefined;
    this.branchId = undefined;
  }

  getDetail(row: any) {
    if (row.booking.services.length) {
      switch (row.operationSource) {
        case 'BOOKING':
          return row.operationDetails.find(
            (f: any) => f.operationType === 'EVENT'
          ).service?.name;
        case 'REALIZATION':
          return row.operationDetails[0].product?.name;
        default:
          return 'N/A';
      }
    }

    return 'N/A';
  }

  changeCalendar($event: (Date | null)[]) {
    this.searchForm.get('date')?.setValue({
      begin: $event[0] && format($event[0], 'yyyy-MM-dd 00:00:00'),
      end: $event[1] && format($event[1], 'yyyy-MM-dd 23:59:59'),
    });
    this.getOperations();
  }

  ngOnDestroy(): void {
    this.sub$.next(null);
    this.sub$.complete();
  }

  viewPackagePurchase(row: { servicePackagePurchaseId?: string }): void {
    openPackagePurchaseDrawer(
      this.drawerService,
      row?.servicePackagePurchaseId
    );
  }

  viewUser(user: User) {
    let title = `${user.firstname} ${user.lastname || ''}`;
    if (user.cardNo) {
      title += ` - # ${user.cardNo}`;
    }
    this.drawerService.create<UserViewComponent, { data: string }, string>({
      nzTitle: title,
      nzContent: UserViewComponent,
      nzWrapClassName: 'user-drawer',
      nzBodyStyle: {
        padding: '0 15px 15px 15px',
      },
      nzData: {
        data: user.id,
      },
    });
  }
}
